import { Accumulator } from "./aggregations";
import { dateBucket } from "./dates";
import type {
  PivotCellValues,
  PivotColumnKey,
  PivotConfig,
  PivotDimension,
  PivotField,
  PivotResult,
  PivotRowNode,
  PivotSort,
} from "./types";
import { BLANK_KEY, KEY_SEPARATOR, OTHER_KEY, TOTAL_KEY } from "./types";

const ROOT = "";
const BLANK = { key: BLANK_KEY, label: "(Blank)" };

type AccMap = Map<string, Accumulator[]>;

export const joinKey = (path: string[]) => path.join(KEY_SEPARATOR);

const ESCAPE = "\u001e";

/** Keeps the separator out of data keys so distinct paths always join to distinct keys */
export const dataKey = (text: string) =>
  text
    .replaceAll(ESCAPE, `${ESCAPE}${ESCAPE}`)
    .replaceAll(KEY_SEPARATOR, `${ESCAPE}s`);

const compareKeys = (a: string, b: string) =>
  a.localeCompare(b, "en-US", { numeric: true, sensitivity: "base" });

export const readField = <TData>(
  row: TData,
  field: PivotField<TData> | undefined,
  id: string,
) =>
  field?.accessor
    ? field.accessor(row)
    : (row as Record<string, unknown> | null)?.[id];

const dimensionResolver =
  <TData>(dimension: PivotDimension, field: PivotField<TData> | undefined) =>
  (row: TData) => {
    const raw = readField(row, field, dimension.field);
    if (raw === null || raw === undefined || raw === "") return BLANK;
    if (field?.type === "date" && dimension.dateGrouping) {
      return dateBucket(raw, dimension.dateGrouping) ?? BLANK;
    }
    const text = String(raw);
    return { key: dataKey(text), label: text };
  };

export const defaultConfig = (
  config: PivotConfig,
): Required<
  Pick<
    PivotConfig,
    "rowSubtotals" | "columnSubtotals" | "rowGrandTotal" | "columnGrandTotal"
  >
> &
  PivotConfig => {
  return {
    rowSubtotals: true,
    columnSubtotals: false,
    rowGrandTotal: true,
    columnGrandTotal: true,
    ...config,
  };
};

const compareNodes =
  (sort: PivotSort | undefined) => (a: PivotRowNode, b: PivotRowNode) => {
    if (a.isOther !== b.isOther) return a.isOther ? 1 : -1;
    if (sort?.by === "value") {
      const column = sort.columnKey ?? TOTAL_KEY;
      const left = a.cells[column]?.[sort.valueId] ?? null;
      const right = b.cells[column]?.[sort.valueId] ?? null;
      if (left !== right) {
        if (left === null) return 1;
        if (right === null) return -1;
        return (left - right) * (sort.desc ? -1 : 1);
      }
    }
    const byLabel = compareKeys(
      a.path[a.path.length - 1],
      b.path[b.path.length - 1],
    );
    return sort?.by === "label" && sort.desc ? -byLabel : byLabel;
  };

export const sortPivotRows = (
  rows: PivotRowNode[],
  sort: PivotSort | undefined,
): PivotRowNode[] =>
  [...rows]
    .sort(compareNodes(sort))
    .map((row) =>
      row.children
        ? { ...row, children: sortPivotRows(row.children, sort) }
        : row,
    );

export const computePivot = <TData>({
  data,
  fields,
  config: rawConfig,
}: {
  data: TData[];
  fields: PivotField<TData>[];
  config: PivotConfig;
}): PivotResult => {
  const config = defaultConfig(rawConfig);
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  const rowResolvers = config.rows.map((dim) =>
    dimensionResolver(dim, fieldById.get(dim.field)),
  );
  const columnResolvers = config.columns.map((dim) =>
    dimensionResolver(dim, fieldById.get(dim.field)),
  );
  const values = config.values;
  const newAccs = () =>
    values.map(
      (value) =>
        new Accumulator(
          value.aggregation === "median",
          value.aggregation === "distinctCount",
        ),
    );

  const accs = new Map<string, AccMap>();
  const rowInfo = new Map<
    string,
    { path: string[]; labels: string[]; count: number }
  >();
  const rowChildren = new Map<string, Set<string>>();
  const columnInfo = new Map<string, { path: string[]; labels: string[] }>();
  const columnChildren = new Map<string, Set<string>>();

  const link = (
    map: Map<string, Set<string>>,
    parent: string,
    child: string,
  ) => {
    let set = map.get(parent);
    if (!set) map.set(parent, (set = new Set()));
    set.add(child);
  };

  for (const record of data) {
    const rowParts = rowResolvers.map((resolve) => resolve(record));
    const columnParts = columnResolvers.map((resolve) => resolve(record));
    const recordValues = values.map((value) =>
      readField(record, fieldById.get(value.field), value.field),
    );

    const rowKeys = [ROOT];
    rowParts.forEach((part, i) => {
      const path = rowParts.slice(0, i + 1).map((p) => p.key);
      const key = joinKey(path);
      link(rowChildren, rowKeys[i], key);
      rowKeys.push(key);
      if (!rowInfo.has(key)) {
        rowInfo.set(key, {
          path,
          labels: rowParts.slice(0, i + 1).map((p) => p.label),
          count: 0,
        });
      }
    });

    const columnKeys = [TOTAL_KEY];
    columnParts.forEach((part, i) => {
      const path = columnParts.slice(0, i + 1).map((p) => p.key);
      const key = joinKey(path);
      link(columnChildren, columnKeys[i], key);
      columnKeys.push(key);
      if (!columnInfo.has(key)) {
        columnInfo.set(key, {
          path,
          labels: columnParts.slice(0, i + 1).map((p) => p.label),
        });
      }
    });

    for (const rowKey of rowKeys) {
      const info = rowInfo.get(rowKey);
      if (info) info.count += 1;
      let byColumn = accs.get(rowKey);
      if (!byColumn) accs.set(rowKey, (byColumn = new Map()));
      for (const columnKey of columnKeys) {
        let cellAccs = byColumn.get(columnKey);
        if (!cellAccs) byColumn.set(columnKey, (cellAccs = newAccs()));
        recordValues.forEach((value, i) => cellAccs![i].add(value));
      }
    }
  }

  const sortedChildren = (map: Map<string, Set<string>>, parent: string) =>
    Array.from(map.get(parent) ?? []).sort(compareKeys);

  const columnKeys: PivotColumnKey[] = [];
  const depth = config.columns.length;
  const walkColumns = (parent: string, level: number) => {
    for (const key of sortedChildren(columnChildren, parent)) {
      const info = columnInfo.get(key)!;
      if (level === depth - 1) {
        columnKeys.push({
          key,
          path: info.path,
          labels: info.labels,
          kind: "leaf",
        });
      } else {
        walkColumns(key, level + 1);
        if (config.columnSubtotals) {
          columnKeys.push({
            key,
            path: info.path,
            labels: info.labels,
            kind: "subtotal",
          });
        }
      }
    }
  };
  if (depth > 0) walkColumns(TOTAL_KEY, 0);
  if (depth === 0 || config.columnGrandTotal) {
    columnKeys.push({ key: TOTAL_KEY, path: [], labels: [], kind: "total" });
  }

  const finalize = (byColumn: AccMap | undefined) => {
    const cells: Record<string, PivotCellValues> = {};
    byColumn?.forEach((cellAccs, columnKey) => {
      const result: PivotCellValues = {};
      values.forEach((value, i) => {
        result[value.id] = cellAccs[i].result(value.aggregation);
      });
      cells[columnKey] = result;
    });
    return cells;
  };

  const mergeAccMaps = (keys: string[]) => {
    const merged: AccMap = new Map();
    for (const key of keys) {
      accs.get(key)?.forEach((cellAccs, columnKey) => {
        let target = merged.get(columnKey);
        if (!target) merged.set(columnKey, (target = newAccs()));
        cellAccs.forEach((acc, i) => target![i].merge(acc));
      });
    }
    return merged;
  };

  const rankBy = (key: string, valueIndex: number) =>
    accs
      .get(key)
      ?.get(TOTAL_KEY)
      ?.[valueIndex].result(values[valueIndex].aggregation) ?? null;

  const buildRows = (
    parent: string,
    level: number,
    parentPath: string[],
  ): PivotRowNode[] => {
    let childKeys = sortedChildren(rowChildren, parent);
    const field = config.rows[level]?.field ?? "";
    const topN = config.topN?.find((rule) => rule.field === field);
    let otherKeys: string[] = [];
    if (topN && childKeys.length > topN.n) {
      const valueIndex = values.findIndex((value) => value.id === topN.valueId);
      if (valueIndex !== -1) {
        const ranked = [...childKeys].sort((a, b) => {
          const left = rankBy(a, valueIndex) ?? -Infinity;
          const right = rankBy(b, valueIndex) ?? -Infinity;
          if (left === right) return 0;
          return topN.bottom ? left - right : right - left;
        });
        const kept = new Set(ranked.slice(0, topN.n));
        otherKeys = topN.showOther === false ? [] : ranked.slice(topN.n);
        childKeys = childKeys.filter((key) => kept.has(key));
      }
    }

    const toNode = (key: string): PivotRowNode => {
      const info = rowInfo.get(key)!;
      const hasChildren = level < config.rows.length - 1;
      return {
        key,
        path: info.path,
        labels: info.labels,
        label: info.labels[info.labels.length - 1],
        field,
        depth: level,
        cells: finalize(accs.get(key)),
        hasChildren,
        children: hasChildren
          ? buildRows(key, level + 1, info.path)
          : undefined,
        recordCount: info.count,
      };
    };

    const nodes = childKeys.map(toNode);
    if (otherKeys.length) {
      const path = [...parentPath, OTHER_KEY];
      const parentLabels = parent === ROOT ? [] : rowInfo.get(parent)!.labels;
      nodes.push({
        key: joinKey(path),
        path,
        labels: [...parentLabels, "Other"],
        label: "Other",
        field,
        depth: level,
        cells: finalize(mergeAccMaps(otherKeys)),
        hasChildren: true,
        children: otherKeys.map(toNode),
        isOther: true,
        recordCount: otherKeys.reduce(
          (total, key) => total + rowInfo.get(key)!.count,
          0,
        ),
      });
    }
    return nodes;
  };

  const rows = config.rows.length ? buildRows(ROOT, 0, []) : [];

  return {
    rows: sortPivotRows(rows, config.sort),
    columnKeys,
    grandTotal: finalize(accs.get(ROOT)),
    recordCount: data.length,
  };
};

const pathMatcher = <TData>(
  dimensions: PivotDimension[],
  fieldById: Map<string, PivotField<TData>>,
  path: string[],
) => {
  const resolvers = dimensions
    .slice(0, path.length)
    .map((dim) => dimensionResolver(dim, fieldById.get(dim.field)));
  return (record: TData) =>
    resolvers.every((resolve, i) => resolve(record).key === path[i]);
};

/** The source records behind one pivot cell (drill to detail) */
export const pivotDetailRows = <TData>({
  data,
  fields,
  config,
  row,
  column,
}: {
  data: TData[];
  fields: PivotField<TData>[];
  config: PivotConfig;
  row: PivotRowNode;
  column: PivotColumnKey;
}) => {
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  // "Other" stands for its children, each a real group
  const rowPaths =
    row.isOther && row.children ? row.children.map((c) => c.path) : [row.path];
  const rowMatchers = rowPaths.map((path) =>
    pathMatcher(config.rows, fieldById, path),
  );
  const columnMatches = pathMatcher(config.columns, fieldById, column.path);
  return data.filter(
    (record) =>
      columnMatches(record) && rowMatchers.some((matches) => matches(record)),
  );
};
